<template>
  <SloModal :maxWidth="850" @formSubmit="download()" @dismiss="cancel()" class="download-design-spec-modal">
    <template v-slot:title>
      {{ 'Download' | t }} <strong v-html="projectName" /> {{ 'Spec' | t }}
    </template>
    <section class="content-container">
      <div :class="{ 'img-container': true, animate: downloading }">
        <div class="stack">
          <img v-for="src of stack" :key="src" :src="'/themes/sloan/img/' + src" alt="Sloan DesignSpec image stack" width="150" height="192" />
        </div>
        <div class="step-container" v-if="downloading">
          <transition mode="out-in" name="step">
            <p class="step" v-if="progress < 100" :key="step">
              <span v-if="step">{{ step }}...</span>
            </p>
            <svg
              class="check"
              v-else
              aria-hidden="true"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                fill="currentColor"
                d="M413.505 91.951L133.49 371.966l-98.995-98.995c-4.686-4.686-12.284-4.686-16.971 0L6.211 284.284c-4.686 4.686-4.686 12.284 0 16.971l118.794 118.794c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-11.314-11.314c-4.686-4.686-12.284-4.686-16.97 0z"
              />
            </svg>
          </transition>
        </div>
        <svg
          v-if="downloading"
          class="indicator"
          width="240"
          height="240"
          viewBox="0 0 240 240"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="120" cy="120" r="116" stroke="#F4F4F5" stroke-width="8" />
          <circle
            cx="120"
            cy="120"
            r="116"
            stroke="#006BB6"
            stroke-width="8"
            :style="{'stroke-dashoffset': 728 + -728 * (progress / 100)}"
          />
        </svg>
      </div>
      <div v-stack-y:lg>
        <p>{{ 'Your spec will be prepared as a .zip package for download.' | t }}</p>
        <p class="assets" v-stack-x:xs>
          <SloIcon name="FolderArrowDown" />
          <span>
            {{ 'Sloan DesignSpec' | t }} - <span v-html="projectName" />
          </span>
        </p>
        <p>
          {{ 'Select additional files to include for each product in your DesignSpec package:' | t }}
        </p>
        <SloCheckList
          :select-all="true"
          v-model="selectedDownloadTypes"
          :items="downloadTypes"
        />
      </div>
    </section>
    <template v-slot:footer>
      <SloButton
        type="button"
        fill="outline"
        @click="cancel()"
        :disabled="downloading"
      >
        {{ 'Cancel' | t }}
      </SloButton>
      <SloButton type="submit" :disabled="downloading">{{ 'Download' | t }}</SloButton>
    </template>
  </SloModal>
</template>

<script>
import {mapGetters} from 'vuex';
import axios from 'axios';
import t from '../../../main/i18n';
import store from '../../../main/vue-store';
import {getProject, getSharedProject} from '../../apis/designspec';
import SloModal from '../../../../lib/components/SloModal.vue';
import SloButton from '../../../../lib/components/SloButton.vue';
import SloCheckList from '../../../../lib/components/SloCheckList.vue';
import SloIcon from '../../../../lib/components/SloIcon.vue';

const num = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);
const sleep = ms => new Promise(res => setTimeout(res, ms));
const CancelToken = axios.CancelToken;

export default {
  components: {
    SloModal,
    SloButton,
    SloCheckList,
    SloIcon
  },
  filters: {
    t
  },
  beforeRouteEnter(to, from, next) {
    const projectId = Number(to.params.id);
    const shareToken = to.params.shareToken;

    if (shareToken) {
      getSharedProject(projectId, shareToken)
        .then(project => next(vm => vm.setProject(project)))
        .catch(() => next(vm => vm.networkError = true));
    } else {
      if (!store.getters['auth/authenticated']) {
        const project = store.getters['designspec/project'](projectId);
        next(vm => vm.setProject(project));
      } else {
        getProject(projectId)
          .then(project => next(vm => vm.setProject(project)))
          .catch(() => next(vm => vm.networkError = true));
      }
    }
  },
  data() {
    return {
      activeRequest: null,
      stack: [
        'design-spec-stack-cover.jpg',
        'design-spec-stack-images.jpg',
        'design-spec-stack-products.jpg',
        'design-spec-stack-spec-sheet.jpg'
      ],
      project: null,
      progress: 0,
      step: null,
      downloading: false,
      selectedDownloadTypes: [],
      downloadTypes: [
        {
          value: 'spec_sheet',
          label: t('Spec Sheet')
        },
        {
          value: 'bim_model',
          label: t('BIM Model')
        },
        {
          value: 'cad_drawing',
          label: t('CAD Drawing')
        },
        {
          value: 'installation_instructions',
          label: t('Installation Instructions')
        },
        {
          value: 'repair_guide',
          label: t('Repair & Maintenance Guide')
        }
      ]
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    projectName() {
      return this.project?.name;
    }
  },
  methods: {
    setProject(project) {
      this.project = project;
    },
    cancel() {
      if (this.activeRequest) {
        this.activeRequest.cancel();
      }
      this.$router.back();
    },
    async downloadFile(file) {
      const a = document.createElement('a');
      a.href = file;
      a.download = '';
      a.addEventListener('click', event => {
        event.stopPropagation();
        a.remove();
      });
      document.body.appendChild(a);
      a.click();
    },
    async download() {
      if (this.downloading) return;
      const project = Object.assign({}, this.project);
      let data = {};
      if (this.authenticated) {
        data = {id: Number(project.id)};
      } else {
        delete project.meta;
        data = Object.assign({}, project);
      }
      Object.assign(data, {
        downloadTypes: this.selectedDownloadTypes
      });

      this.progress = 0;
      this.downloading = true;
      this.activeRequest = CancelToken.source();

      let request = axios
        .post(this.authenticated ? '/designspec/api/download/build' : '/designspec/api/local/download/build',
          data,
          {
            responseType: 'json',
            cancelToken: this.activeRequest.token
          }
        )
        .catch(e => {
          if (axios.isCancel(e)) {
            return;
          }

          this.progress = 0;
          this.downloading = false;
          this.activeRequest = null;
          this.$snotify.error(`${t('Unable to download')} ${t('Sloan DesignSpec')} - ${this.projectName}`);
        })
        .then(response => {
          if (!response) {
            this.$snotify.error(`${t('Unable to download')} ${t('Sloan DesignSpec')} - ${this.projectName}`);
            return;
          }

          return new Promise(res => {
            let interval = setInterval(() => {
              if (this.progress > 30) {
                clearInterval(interval);
                res(response.data);
              }
            }, 300);
          });
        })
        .then(data => {
          if (!data) {
            this.$snotify.error(`${t('Unable to download')} ${t('Sloan DesignSpec')} - ${this.projectName}`);
            return;
          }

          if (data.hasDisabledProducts) {
            this.$snotify.info(`${t('Some products in your DesignSpec project have been discontinued and were removed.')}`);
          }

          this.downloadFile(data.path);
        });

      try {
        await Promise.race([request, this.incrementProgress()]);
      } catch (e) {
        this.$snotify.error(`${t('Unable to download')} ${t('Sloan DesignSpec')} - ${this.projectName}`);
      }

      await request;
      this.step = null;
      this.progress = 100;
      await sleep(1000);
      this.downloading = false;
      this.activeRequest = null;
      this.$router.back();
    },
    async incrementProgress() {
      const endProgress = [80, 90];
      const steps = [
        'Finding products',
        'Collecting assets',
        'Optimizing assets',
        'Creating archive',
        'Optimizing bundle'
      ];

      this.progress = 5;
      this.step = steps[0];
      await sleep(num(1500, 2500));
      this.progress += num(5, 15);
      await sleep(num(1500, 3000));
      this.step = steps[1];
      this.progress += num(10, 21);
      await sleep(num(1500, 3000));
      this.step = steps[2];
      this.progress += num(15, 32);
      await sleep(num(2000, 2700));
      this.step = steps[3];

      const remaining = (endProgress[0] - this.progress) / 5;

      const promises = Array.from({length: 5}, () => {
        return () =>
          sleep(num(1500, 5000)).then(() => {
            if (this.progress === 100) return;
            const newProgress = this.progress + num(remaining * 0.7, remaining * 1.3);
            this.progress = Math.min(newProgress, endProgress[0]);
          });
      });

      for (const p of promises) {
        await p();
      }

      this.step = steps[4];
      this.progress = num(...endProgress);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../../scss/variables';
@import '../../../../scss/system';

.content-container {
  display: flex;
  flex-direction: row;
}

.img-container {
  position: relative;
  align-items: center;
  justify-content: center;

  display: none;

  @media screen and (min-width: $screen-sm-min) {
    display: flex;
  }
}

.img-container .stack {
  position: relative;
  width: 270px;
  height: 270px;
}

.indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  width: 200px;
  height: 200px;
  opacity: 0;
  animation: fade-in 0.5s 1s forwards, spin 1.3s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.step-container {
  opacity: 0;
  animation: fade-in 0.5s 1s forwards;
}

.check {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  color: $color-blue-400;
}

.step {
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @include text('lg');
}

.indicator circle:nth-child(2) {
  stroke-dasharray: 704;
  transition: stroke-dashoffset 1s cubic-bezier(0.23, 1, 0.32, 1);
}

.assets {
  display: flex;
  align-items: center;
  @include text('base', 'strong');

  .slo-icon {
    color: $color-gray-700;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-out {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.25);
  }
}

@keyframes fade-enter {
  from {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

@keyframes fade-exit {
  from {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -60%);
  }
}

@keyframes spin {
  from {
    transform: translate(-50%, -50%) rotate(-90deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(270deg);
  }
}

.step-enter-active {
  animation: 300ms cubic-bezier(0.23, 1, 0.32, 1) fade-enter;
}

.step-leave-active {
  animation: 200ms cubic-bezier(0.23, 1, 0.32, 1) fade-exit;
}

.stack img {
  max-width: 150px;
  position: absolute;
  transition: transform 350ms ease-out, opacity 200ms ease-out;
}

.animate .stack img {
  transform: translateY(50%);
  opacity: 0;
}

.stack > img:nth-child(1) {
  top: 0;
  right: 0;
  z-index: 4;
  transition-delay: 350ms;
}

.stack > img:nth-child(2) {
  top: 25px;
  right: 40px;
  z-index: 3;
  transition-delay: 250ms;
}

.stack > img:nth-child(3) {
  bottom: 25px;
  left: 40px;
  z-index: 2;
  transition-delay: 150ms;
}

.stack > img:nth-child(4) {
  bottom: 0;
  left: 0;
  z-index: 1;
  transition-delay: 50ms;
}

.content-container > div {
  flex: 1 0 50%;
}

ul {
  padding: 0;

  li {
    list-style: none;
  }
}
</style>
